/**
 * The coffee machine as the store and the UI see it: what a Manetta device (taretto-app/manetta, SPEC.md) reports and
 * can do, in Taretto's own words. Nothing here imports @taretto/manetta, so the store can load the driver (and the
 * protobuf runtime it brings) only when someone connects a machine.
 */
import type { MachineStopReason } from '@shared/schemas';

export type { MachineStopReason };

/** What the machine is doing (Manetta's Phase). A phase this version doesn't know reads as brewing (SPEC 3.4). */
export type MachinePhase = 'idle' | 'brewing' | 'fault';

/**
 * Whether the machine is on (Manetta's PowerState). `on`: on, and the device can't tell heating from ready, or the
 * machine is in a mode such as steam. A state this version doesn't know reads as `on`.
 */
export type MachinePower = 'off' | 'heating' | 'ready' | 'cooling' | 'on';

/** Brew heat or steam heat (Manetta's SteamState). A state this version doesn't know reads as heating (steam-mode.md). */
export type MachineSteam = 'off' | 'heating' | 'ready';

/** The power capability (power.md): each part needs the one before. */
export interface MachinePowerControl {
  /** The device can turn the machine off (`power.off`). */
  turnOff: boolean;
  /** The device can turn it on as well (`power.off.on`). */
  turnOn: boolean;
  /** The device's own auto-off after a turn on, in minutes (`power.off.on.auto_off_min`). */
  autoOffMin: number | null;
  /** The device goes off with the machine, so it disconnects after Turn off. */
  goesOffWithMachine: boolean;
}

/** What the device lists in its DeviceInfo (SPEC 3.2). Taretto offers the controls of these, and no others. */
export interface MachineCapabilities {
  /** `brew_start_stop`: start and stop a brew. */
  brew: boolean;
  /** `power`: null without it; otherwise it reports the power state, and maybe turns the machine off and on. */
  power: MachinePowerControl | null;
  /** `steam_mode`: switch between brew heat and steam heat. */
  steam: boolean;
}

export interface MachineInfo {
  /** The machine, as whoever installed the firmware describes it, e.g. "HiBrew H10B". May be empty. */
  model: string;
  /** e.g. "manetta-esp32 0.3.0", for Settings › About and bug reports. */
  firmware: string;
  /** The Manetta release the firmware was built against. For diagnostics only, never for features (SPEC 6.3). */
  protocolVersion: string;
  heartbeatTimeoutMs: number;
  capabilities: MachineCapabilities;
}

/** The last Status (MachineStatus), mapped. */
export interface MachineState {
  phase: MachinePhase;
  /** null: the device has no power capability, or can't tell right now. Never read as off (power.md). */
  power: MachinePower | null;
  /** null without steam_mode. */
  steam: MachineSteam | null;
  /**
   * performance.now() when the machine went on, from `on_elapsed_s` (counted on locally from the last value, as
   * power.md says). null while it's off, or when the device doesn't know since when.
   */
  onSince: number | null;
  /** A change means the device restarted. */
  bootId: number;
  /** The brew running now, or the last one. */
  brewId: number;
  /** How long that brew has run, or ran, in ms (counted by the device), when this Status came. */
  elapsedMs: number;
  /** performance.now() when this Status came. */
  at: number;
  /** Who started that brew: Taretto or another app (`app`), or the machine's own controls. null before the first. */
  startSource: 'app' | 'machine' | null;
  /** What stopped that brew. null while it runs, or when the device doesn't say. */
  stopReason: MachineStopReason | null;
}

/**
 * The commands send() takes. start_brew and enter_steam_mode have calls of their own (startBrew, enterSteamMode), for
 * their caps and start_brew's brew_id.
 */
export type MachineCommand = 'turnOn' | 'turnOff' | 'stopBrew' | 'leaveSteamMode' | 'ping';

/**
 * The device's answer (Manetta's CommandResult), or `no_reply` when none came after the retries. `ok` means the
 * device took the command, not that the machine got there: Status says when it has.
 */
export type MachineCommandResult = 'ok' | 'unsupported' | 'invalid' | 'busy' | 'not_ready' | 'fault' | 'no_reply';

export interface MachineConnectOptions {
  /** Give up after this long (auto-connect and background retries). */
  timeoutMs?: number;
}

/** A Manetta device over one BluetoothDevice (manetta.ts), the same for the dev build's virtual machine. */
export interface MachineDriver {
  /** The advertised name, else the model. */
  readonly name: string;
  /** The Web Bluetooth device id, which `getDevices()` gives back on the next visit (Chrome). */
  readonly deviceId: string;
  readonly connected: boolean;
  /** Read on every connect (SPEC 3.2); null before the first. */
  readonly info: MachineInfo | null;
  readonly state: MachineState | null;
  /** Control is subscribed on this connection, so the link is bonded. False until the first command after a connect. */
  readonly paired: boolean;
  connect(opts?: MachineConnectOptions): Promise<void>;
  disconnect(): Promise<void>;
  onState(cb: (state: MachineState) => void): () => void;
  /** The link dropped, or the device went off with the machine. Not called for disconnect(). */
  onDisconnect(cb: () => void): () => void;
  /**
   * Sends one command and resolves with the device's answer. The first one on a connection pairs if needed. A
   * stop_brew goes ahead of anything waiting (SPEC 3.3).
   */
  send(command: MachineCommand): Promise<MachineCommandResult>;
  /** start_brew, capped at `maxDurationMs`: the answer, and the new brew's id when it's `ok`. */
  startBrew(maxDurationMs: number): Promise<{ result: MachineCommandResult; brewId: number | null }>;
  /** enter_steam_mode, back to brew heat by itself after `maxDurationMs` (steam-mode.md). */
  enterSteamMode(maxDurationMs: number): Promise<MachineCommandResult>;
}

/** Subscribing to Control failed: the OS couldn't pair, or the device refused it outside its pairing window. */
export class MachinePairingError extends Error {
  constructor() {
    super('Pairing didn’t finish. Unplug the machine, plug it back in, then try again within a minute.');
    this.name = 'MachinePairingError';
  }
}

/** The device speaks no wire major this version knows (SPEC 3.2), so Taretto offers no controls. */
export class MachineTooNewError extends Error {
  constructor(majors: readonly number[]) {
    const speaks = majors.length > 0 ? `It speaks Manetta ${majors.join(', ')}, and` : 'It speaks another Manetta, and';
    super(`This machine needs a newer Taretto. ${speaks} this version knows Manetta 1.`);
    this.name = 'MachineTooNewError';
  }
}
